/**
 * useCitationVerificationBadges — decorates rendered citation pills with
 * the fact-checker's verdicts.
 *
 * The deliverable markdown renders through ReactMarkdown → `CitationLink`,
 * which emits `<span class="supContainer" data-citation-raw="...">`. Rather
 * than plumbing the verification map through every render path, we apply
 * the badges via a DOM effect: when the verification map changes, walk
 * the document, match by the raw citation string, and set data-verified
 * + data-verification-issue attributes. CSS (see deep-explorer.css
 * additions) then styles the badge.
 *
 * QA #SP2: a `MutationObserver` watches the document for newly-inserted
 * `[data-citation-raw]` nodes (e.g. when a section collapses + re-expands,
 * or a deliverable tab re-mounts) and re-stamps them with the latest
 * verdicts. Without this, freshly-mounted CitationLinks show no verification
 * pill until the next server verdict arrives because the parent effect
 * doesn't re-fire when the map hasn't changed.
 *
 * Why MutationObserver and not React context: `CitationLink` is shared by
 * chat / canvas / tender / system-message paths whose owners forbid
 * cross-cutting refactors here. The observer is contained, has no
 * cross-feature blast radius, and is safe — it only sets data-* attributes.
 *
 * Safe because:
 * - We only read the map; we never mutate any React-managed state.
 * - We only set data-* attributes + no structural DOM changes, so React's
 *   reconciliation is undisturbed.
 * - On re-render, CitationLink resets the DOM to its base state; the
 *   observer re-applies on the next mutation.
 */
import { useEffect } from "react"

import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"

const CITATION_SELECTOR = "[data-citation-raw]"
type VerificationMap = Record<string, { verified: boolean | null; issue?: string }>

function lookupVerification(
  map: VerificationMap,
  key: string
): { verified: boolean | null; issue?: string } | undefined {
  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key]
  return undefined
}

function stampPill(pill: HTMLElement, verifications: VerificationMap): void {
  const hasAny = Object.keys(verifications).length > 0
  const raw = pill.getAttribute("data-citation-raw") ?? ""
  if (!raw) return
  if (!hasAny) {
    pill.removeAttribute("data-verified")
    pill.removeAttribute("data-verification-issue")
    return
  }
  const verdict =
    lookupVerification(verifications, raw) ??
    // Fall back to matching without the quote part so citations that
    // had their quote stripped still find a verdict.
    matchByPrefix(verifications, raw)
  if (!verdict) {
    pill.removeAttribute("data-verified")
    pill.removeAttribute("data-verification-issue")
    return
  }
  if (verdict.verified === true) {
    pill.setAttribute("data-verified", "true")
  } else if (verdict.verified === false) {
    pill.setAttribute("data-verified", "false")
  } else {
    pill.setAttribute("data-verified", "unknown")
  }
  if (verdict.issue) {
    pill.setAttribute("data-verification-issue", verdict.issue)
  } else {
    pill.removeAttribute("data-verification-issue")
  }
}

export function useCitationVerificationBadges(
  sessionType: string,
  options?: { disabled?: boolean }
): void {
  const store = useAIExplorerStore(sessionType)
  const verifications = store(
    (s) => getDeepStateForSession(s, s.currentSessionId).citationVerifications
  )

  useEffect(() => {
    if (typeof document === "undefined") return
    if (options?.disabled) return
    const pills = document.querySelectorAll<HTMLElement>(CITATION_SELECTOR)
    pills.forEach((pill) => stampPill(pill, verifications))

    // Re-stamp when CitationLinks re-mount. Without this, a section collapse
    // + re-expand or any tree replacement that re-creates the pill DOM ends
    // up with blank verification badges (the pill has fresh attributes; the
    // parent effect only runs on `verifications` change). (#SP2)
    if (typeof MutationObserver === "undefined") return
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue
          if (node.matches(CITATION_SELECTOR)) {
            stampPill(node, verifications)
          }
          const inner = node.querySelectorAll<HTMLElement>(CITATION_SELECTOR)
          inner.forEach((pill) => stampPill(pill, verifications))
        }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [options?.disabled, verifications])
}

function matchByPrefix(
  map: VerificationMap,
  raw: string
): { verified: boolean | null; issue?: string } | undefined {
  // Citations can differ by whitespace or truncated quotes between the
  // rendered form and the one the fact-checker wrote. Try a prefix match
  // on the source identifier (everything before the first `|`).
  const sourceId = raw.split("|", 1)[0]
  if (!sourceId) return undefined
  for (const key of Object.keys(map)) {
    if (key.startsWith(sourceId)) return lookupVerification(map, key)
  }
  return undefined
}
